import { useState } from 'react';
import './cardBaseball.sass';

/**
 * Componente que muestra los lanzadores probables de un juego de baseball
 * Renderiza información de ambos pitchers (izquierdo y derecho) con imagen, nombre y estadísticas
 * Maneja estados de error de imagen y casos donde el pitcher no está definido
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.item - Datos del juego de baseball
 * @param {Object} props.item.baseball_data - Datos específicos del baseball
 * @param {Object} props.item.baseball_data.left_stats - Estadísticas del equipo izquierdo
 * @param {Object} props.item.baseball_data.left_stats.start_pitcher - Datos del pitcher titular izquierdo
 * @param {Object} props.item.baseball_data.right_stats - Estadísticas del equipo derecho
 * @param {Object} props.item.baseball_data.right_stats.start_pitcher - Datos del pitcher titular derecho
 * @param {string} props.item.left_short_name - Nombre corto del equipo izquierdo
 * @param {string} props.item.right_short_name - Nombre corto del equipo derecho
 * 
 * @example
 * <PitchersComponent 
 *   item={{
 *     baseball_data: {
 *       left_stats: { start_pitcher: { player: { firstName: "John", lastName: "Doe" }, wins: 10, losses: 5 } },
 *       right_stats: { start_pitcher: { player: { firstName: "Jane", lastName: "Smith" }, wins: 8, losses: 7 } }
 *     },
 *     left_short_name: "NYY",
 *     right_short_name: "BOS"
 *   }} 
 * />
 * 
 * @returns {JSX.Element} Componente con información de ambos lanzadores probables
 */
export default function PitchersComponent({ item = {} }) {
  // Validación de datos principales
  const { baseball_data = {}, right_short_name = '', left_short_name = '' } = item;
  const { left_stats = {}, right_stats = {} } = baseball_data;
  const [imageErrors, setImageErrors] = useState({ left: false, right: false });

  // Si no hay datos de baseball, no renderizar nada
  if (!baseball_data || (!left_stats && !right_stats)) {
    return null;
  }

  /**
   * Renderiza la información de un pitcher individual
   * Maneja casos de imagen faltante/error y pitcher no definido
   * 
   * @param {Object} stats - Estadísticas del equipo (left_stats o right_stats)
   * @param {string} position - Posición del pitcher ('left' o 'right')
   * @param {string} shorName - Nombre corto del equipo
   * @returns {JSX.Element|null} Elemento JSX del pitcher o null si no hay datos
   */
  const renderPitcher = (stats, position, shorName) => {
    // Validaciones de entrada
    if (!stats || !position || !shorName) return null;
    
    const pitcher = stats.start_pitcher;
    if (!pitcher) return null;

    // Extracción segura de datos con valores por defecto
    const { 
      player = {}, 
      wins = 0, 
      losses = 0, 
      earnedRunAverage = '', 
      image = '' 
    } = pitcher;
    
    const { 
      firstName = '', 
      lastName = '', 
      uniform = '' 
    } = player;

    // Validaciones y formateo
    const displayLastName = lastName && lastName.length > 10 
      ? `${lastName.substring(0, 10)}...` 
      : lastName;
    
    const hasError = imageErrors[position];
    const isPitcherUndefined = !uniform && wins === 0 && losses === 0 && !earnedRunAverage;
    const hasWinsLosses = (typeof wins === 'number' && wins > 0) || (typeof losses === 'number' && losses > 0);
    const hasERA = earnedRunAverage && 
                   earnedRunAverage !== '' && 
                   earnedRunAverage !== '0.00' && 
                   earnedRunAverage !== 0;

    return (
      <div className={`pitcher-container pitcher-${position}`}>
        {hasError || !image ? (
          <div className="pitcher-image none-image"></div>
        ) : (
          <img
            src={image}
            alt={`${firstName} ${lastName}`}
            className="pitcher-image"
            loading="lazy"
            onError={() => setImageErrors(prev => ({ ...prev, [position]: true }))}
          />
        )}
        <div className="pitcher-info">
          <p className="pitcher-name">
            {isPitcherUndefined 
              ? 'POR DEFINIR' 
              : `${firstName ? firstName.charAt(0) : ''}. ${displayLastName || 'Desconocido'}`
            }
          </p>
          <p className='pitcher-team'>
            {shorName || 'Equipo'}{!isPitcherUndefined && uniform && ` - #${uniform}`}
          </p>
          <p className={isPitcherUndefined || (!hasWinsLosses && !hasERA) ? "pitcher-stats-none" : "pitcher-stats"}>
            {isPitcherUndefined ? '-' : (
              <>
                {hasWinsLosses && `(${wins || 0}-${losses || 0}`}
                {hasWinsLosses && hasERA && ', '}
                {hasERA && earnedRunAverage}
                {hasWinsLosses && ')'}
                {!hasWinsLosses && !hasERA && '-'}
              </>
            )}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="pitchers-component">
      <p className='title-pitchers'>LANZADORES PROBABLES</p>
      <div className='section-pitchers'>
        {renderPitcher(left_stats, 'left', left_short_name)}
        {renderPitcher(right_stats, 'right', right_short_name)}
      </div>
    </div>
  );
}